import type * as React from 'react';

import { uploadFile } from '@gitbook/app-database';
import { Link } from '@gitbook/react-uikit';
import { generateKey } from '@gitbook/slate';
import { FilesDropZone, type IconDefinition, toast } from '@gitbook/spine-react';

import { getHumanFileSize } from '../../utils';
import { IMPORT_FILE_MAX_SIZE } from './constants';
import type { GetSpace, PendingFile } from './types';
import { UploadFilesList } from './UploadFilesList';

/**
 * Prompt to import a file with specific instructions.
 */
export function FilesUpload(props: {
    getSpace: GetSpace;

    files: PendingFile[];
    setFiles: React.Dispatch<React.SetStateAction<PendingFile[]>>;

    /** Type of file to upload */
    fileIcon: IconDefinition;
    fileExtension: string;

    /** Show a warning about the maximum number of pages/files */
    limitWarning?: boolean;

    /** Limit the number of files that can be uploaded */
    fileUploadsLimit?: number;
}): React.ReactElement {
    const {
        getSpace,
        files,
        setFiles,
        fileIcon,
        fileExtension,
        limitWarning = false,
        fileUploadsLimit = Number.POSITIVE_INFINITY,
    } = props;

    /** Update a file */
    const updateFile = (key: string, update: Partial<PendingFile>) => {
        setFiles((prev) => prev.map((file) => (file.key === key ? { ...file, ...update } : file)));
    };

    /** Upload a file */
    const startUpload = async (input: PendingFile) => {
        await toast.catch(async () => {
            try {
                const space = await getSpace();
                const uploaded = await uploadFile(
                    {
                        kind: 'space_import',
                        space: space.id,
                    },
                    input.file
                );
                updateFile(input.key, { uploaded });
            } catch (error: unknown) {
                updateFile(input.key, { failed: true });
                throw error;
            }
        }, 'Error while uploading file');
    };

    /** When users dropped some files */
    const onSelectFiles = (inputs: File[]) => {
        const newFiles: PendingFile[] = [];
        for (const input of inputs) {
            if (files.length >= fileUploadsLimit) {
                toast.error('Cannot upload file', null, {
                    description: `You can only upload ${fileUploadsLimit} file for this operation`,
                });
                break;
            }

            if (input.size > IMPORT_FILE_MAX_SIZE) {
                toast.error('Cannot upload file', null, {
                    description: `Your file must be less than ${getHumanFileSize(
                        IMPORT_FILE_MAX_SIZE
                    )}`,
                });

                continue;
            }

            const file = {
                key: generateKey(),
                file: input,
            };

            newFiles.push(file);
        }

        setFiles((prevFiles) => prevFiles.concat(newFiles));
        newFiles.forEach((file) => startUpload(file));
    };

    return (
        <>
            <div>
                <FilesDropZone
                    icon={fileIcon}
                    onSelectFiles={onSelectFiles}
                    label={`Drop your ${fileExtension} file here or choose a file`}
                />
            </div>
            {limitWarning ? (
                <div className="mt-2">
                    <span className="text-ui-small text-ui-text-muted">
                        GitBook has limits for imported content,{' '}
                        <Link url="https://gitbook.com/docs/features/import#limits">
                            read more here.
                        </Link>
                    </span>
                </div>
            ) : null}
            {files.length > 0 ? (
                <div className="mt-4">
                    <UploadFilesList fileIcon={fileIcon} files={files} setFiles={setFiles} />
                </div>
            ) : null}
        </>
    );
}
